import { useEffect, useMemo, useState } from 'react'
import { api } from './api.js'
import { pages, visible } from './conditions.js'
import { navigate } from './App.jsx'
import Champ from './Champ.jsx'
import Creneaux from './Creneaux.jsx'

// Formulaire en étapes. Les questions masquées par une règle ne sont jamais
// obligatoires : elles disparaissent sans bloquer personne.
// Reprise de la vérification côté serveur, pour prévenir avant l'envoi. Le
// serveur reste seul juge : ceci n'est qu'un confort.
function verifierFormat(champ, valeur) {
  const texte = valeur == null ? '' : String(valeur).trim()
  if (!texte || !champ.format) return null
  const chiffres = texte.replace(/[^0-9]/g, '')
  const propre = texte.replace(/\s/g, '').toUpperCase()
  const ok = {
    email: () => /^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(texte),
    tel_fr: () => /^0[1-9][0-9]{8}$/.test(chiffres),
    mobile_fr: () => /^0[67][0-9]{8}$/.test(chiffres),
    code_postal: () => /^[0-9]{5}$/.test(chiffres),
    siret: () => chiffres.length === 14 && luhn(chiffres),
    siren: () => chiffres.length === 9 && luhn(chiffres),
    nir: () => nir(propre),
    ants: () => /^[A-Z0-9]{10}$/.test(propre),
    caf: () => /^[0-9]{7}$/.test(chiffres),
    fiscal: () => /^[0-9]{13}$/.test(chiffres),
    ref_avis: () => propre.length === 13,
    rna: () => /^W[0-9]{9}$/.test(propre),
    iban: () => iban(propre),
    immatriculation: () => /^[A-Z]{2}-?[0-9]{3}-?[A-Z]{2}$/.test(propre),
    entier: () => /^-?[0-9]+$/.test(texte),
    regex: () => { try { return new RegExp(`^(?:${champ.pattern})$`).test(texte) } catch { return true } },
  }[champ.format]
  if (!ok) return null
  return ok() ? null : (champ.format_erreur || champ.pattern_message
                        || "Cette réponse n'a pas le format attendu.")
}

function luhn(n) {
  let total = 0, pair = false
  for (let i = n.length - 1; i >= 0; i--) {
    let c = Number(n[i])
    if (pair) { c *= 2; if (c > 9) c -= 9 }
    total += c
    pair = !pair
  }
  return total % 10 === 0
}

function nir(v) {
  if (!/^[12][0-9]{4}(2[AB]|[0-9]{2})[0-9]{6}[0-9]{2}$/.test(v)) return false
  const corps = v.slice(0, -2).replace('2A', '19').replace('2B', '18')
  return Number(v.slice(-2)) === 97 - (Number(corps) % 97)
}

function iban(v) {
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(v)) return false
  const d = v.slice(4) + v.slice(0, 4)
  const n = d.replace(/[A-Z]/g, (c) => c.charCodeAt(0) - 55)
  let reste = 0
  for (const c of n) reste = (reste * 10 + Number(c)) % 97
  return reste === 1
}

export default function Demarche({ slug }) {
  const [form, setForm] = useState(null)
  const [data, setData] = useState({})
  const [etape, setEtape] = useState(0)
  const [creneau, setCreneau] = useState(null)
  const [manque, setManque] = useState([])
  const [fautes, setFautes] = useState({})
  const [recu, setRecu] = useState(null)
  const [erreur, setErreur] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const [indisponible, setIndisponible] = useState(null)

  useEffect(() => {
    api.form(slug).then(setForm).catch((e) => {
      // Une démarche suspendue n'est pas une démarche qui n'existe pas :
      // on l'explique plutôt que d'afficher une page introuvable.
      if (e.indisponible) setIndisponible(e.indisponible)
      else setErreur(e.message)
    })
  }, [slug])

  const etapes = useMemo(() => {
    if (!form) return []
    const out = pages(form.fields).map((p) => ({ kind: 'page', titre: p.titre || p.title || 'Votre demande', page: p }))
    if (form.agenda) out.push({ kind: 'rdv', titre: 'Créneau' })
    out.push({ kind: 'recap', titre: 'Vérification' })
    return out
  }, [form])

  if (indisponible) return (
    <div className="wrap">
      <section className="indisponible">
        <h1>{indisponible.title || 'Démarche momentanément indisponible'}</h1>
        <p>{indisponible.message}</p>
        {indisponible.reason && <p className="motif">{indisponible.reason}</p>}
        <p>Vous pouvez revenir plus tard, ou contacter la mairie si votre demande est urgente.</p>
        <button className="btn pri" onClick={() => navigate('/')}>Retour aux démarches</button>
      </section>
    </div>
  )
  if (erreur) return <div className="wrap"><p className="erreur">{erreur}</p></div>
  if (!form) return <div className="wrap"><p>Chargement…</p></div>

  if (recu) return (
    <div className="wrap">
      <section className="recu">
        <h1>Demande enregistrée</h1>
        <p className="numero">{recu.number}</p>
        <p className="code">Code de suivi : <strong>{recu.track_code}</strong></p>
        <p>{recu.confirm}</p>
        <p className="note">Conservez ce numéro et ce code : ils suffisent à suivre votre dossier,
          sans créer de compte.</p>
        <div className="actions">
          <button className="btn pri" onClick={() => navigate(`/suivi?n=${recu.number}&c=${recu.track_code}`)}>
            Suivre ce dossier
          </button>
          <button className="btn" onClick={() => navigate('/')}>Retour aux démarches</button>
        </div>
      </section>
    </div>
  )

  const courante = etapes[etape]

  function suivant() {
    if (courante.kind === 'page') {
      const requis = courante.page.fields.filter(
        (f) => f.required && visible(f.condition, data) &&
          (data[f.varname] == null || data[f.varname] === '' ||
            (Array.isArray(data[f.varname]) && !data[f.varname].length)))
      if (requis.length) {
        setManque(requis.map((f) => f.varname))
        document.getElementById('champ-' + requis[0].varname)?.focus()
        return
      }

      // Le format se vérifie ici plutôt qu'à l'envoi : signaler une erreur de
      // frappe trois pages plus loin obligerait à revenir en arrière.
      const mauvais = {}
      for (const f of courante.page.fields) {
        if (!visible(f.condition, data)) continue
        const message = verifierFormat(f, data[f.varname])
        if (message) mauvais[f.varname] = message
      }
      if (Object.keys(mauvais).length) {
        setFautes(mauvais)
        document.getElementById('champ-' + Object.keys(mauvais)[0])?.focus()
        return
      }
    }
    if (courante.kind === 'rdv' && !creneau) {
      setErreur('Choisissez un créneau pour continuer.')
      return
    }
    setManque([])
    setFautes({})
    setErreur('')
    setEtape(etape + 1)
  }

  async function envoyer() {
    setEnvoi(true)
    try {
      const res = await api.submit(slug, { data, booking: creneau })
      setRecu({ ...res })
    } catch (e) {
      // Le serveur revalide tout : s'il refuse un format, on ramène la personne
      // à la question concernée plutôt que d'afficher un message obscur.
      if (e.champs) {
        setFautes(e.champs)
        const premier = Object.keys(e.champs)[0]
        const index = pages.findIndex((p) => p.kind === 'page'
          && p.page.fields.some((f) => f.varname === premier))
        if (index >= 0) setEtape(index)
        setErreur('')
        setTimeout(() => document.getElementById('champ-' + premier)?.focus(), 60)
      } else {
        setErreur(e.message)
      }
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div className="wrap">
      <h1>{form.title}</h1>
      <p className="chapo">{form.description}</p>

      <ol className="etapes">
        {etapes.map((e, i) => (
          <li key={i} className={i === etape ? 'on' : i < etape ? 'faite' : ''}
              aria-current={i === etape ? 'step' : undefined}>
            <span>{i < etape ? '✓' : i + 1}</span>{e.titre}
          </li>
        ))}
      </ol>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {courante.kind === 'page' && (
        <div className="formulaire">
          {courante.page.fields.filter((f) => visible(f.condition, data)).map((f) => (
            <Champ key={f.varname || f.label} champ={f} valeur={data[f.varname]}
                   manquant={manque.includes(f.varname)}
                   faute={fautes[f.varname]}
                   onChange={(v) => setData({ ...data, [f.varname]: v })} />
          ))}
        </div>
      )}

      {courante.kind === 'rdv' && (
        <Creneaux agenda={form.agenda} choisi={creneau} onChoix={setCreneau} />
      )}

      {courante.kind === 'recap' && (
        <table className="recap">
          <caption>Vérifiez vos réponses avant l'envoi</caption>
          <tbody>
            {form.fields.filter((f) => f.varname && visible(f.condition, data)).map((f) => (
              <tr key={f.varname}>
                <th scope="row">{f.label}</th>
                <td>{Array.isArray(data[f.varname]) ? data[f.varname].join(', ')
                  : data[f.varname] || <em>non renseigné</em>}</td>
              </tr>
            ))}
            {creneau && (
              <tr><th scope="row">Rendez-vous</th><td>{creneau.day} à {creneau.time}</td></tr>
            )}
          </tbody>
        </table>
      )}

      <div className="actions">
        <button className="btn" onClick={() => etape === 0 ? navigate('/') : setEtape(etape - 1)}>
          {etape === 0 ? 'Annuler' : 'Étape précédente'}
        </button>
        {courante.kind === 'recap'
          ? <button className="btn pri" onClick={envoyer} disabled={envoi}>
              {envoi ? 'Envoi…' : 'Envoyer ma demande'}
            </button>
          : <button className="btn pri" onClick={suivant}>Continuer</button>}
      </div>

      {courante.kind === 'recap' && form.mention_donnees && (
        <p className="mention-donnees">{form.mention_donnees}</p>
      )}
    </div>
  )
}
